/**
 * <s-product-gallery> — PDP gallery that shows one media item at a time.
 *
 * The track is a native scroll-snap scroller, so touch swipe and trackpad
 * scrolling work without JavaScript. This component only adds:
 *   - prev/next commands (`command="--prev"` / `command="--next"`)
 *   - thumbnail commands (`command="--go"` with `data-index`)
 *   - syncing the active thumbnail and arrow disabled state with scroll position
 *
 * Required markup:
 *   [data-gallery-track]          — the horizontal scroll-snap container
 *   [data-gallery-slide]          — each slide inside the track
 *   button[data-gallery-thumb]    — optional thumbnails, in slide order
 *   button[command="--prev"|"--next"] — optional arrows
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #frame;

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const options = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = [...this.#track.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('button[data-gallery-thumb]')];

    this.addEventListener('command', (event) => {
      if (event.command === '--prev') this.#goTo(this.#index - 1);
      else if (event.command === '--next') this.#goTo(this.#index + 1);
      else if (event.command === '--go') {
        const index = Number(event.source?.dataset.index);
        if (Number.isInteger(index)) this.#goTo(index);
      }
    }, options);

    this.#track.addEventListener('scroll', () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = undefined;
        this.#syncFromScroll();
      });
    }, { ...options, passive: true });

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  #goTo(index) {
    if (!this.#slides.length) return;
    const clamped = Math.max(0, Math.min(this.#slides.length - 1, index));
    const slide = this.#slides[clamped];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      left: slide.offsetLeft - this.#track.offsetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.#index = clamped;
    this.#update();
  }

  #syncFromScroll() {
    const width = this.#track.clientWidth;
    if (!width) return;
    const index = Math.round(this.#track.scrollLeft / width);
    if (index !== this.#index) {
      this.#index = Math.max(0, Math.min(this.#slides.length - 1, index));
      this.#update();
    }
  }

  #update() {
    this.#slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== this.#index);
    });
    this.#thumbs.forEach((thumb, i) => {
      if (i === this.#index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const prev = this.querySelector('button[command="--prev"]');
    const next = this.querySelector('button[command="--next"]');
    if (prev) prev.disabled = this.#index === 0;
    if (next) next.disabled = this.#index >= this.#slides.length - 1;

    const activeThumb = this.#thumbs[this.#index];
    const strip = activeThumb?.parentElement;
    if (activeThumb && strip && strip.scrollWidth > strip.clientWidth) {
      strip.scrollTo({ left: activeThumb.offsetLeft - strip.clientWidth / 2 + activeThumb.clientWidth / 2 });
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
